import React, { useMemo } from "react";
import { Box } from "@mui/material";
import { ColumnDef } from "@tanstack/react-table";
import { IconNoData } from "src/components/icons-mui/no-data";
import { makeTableScrollableLoadingColumns } from "src/components-mui/base/table-scrollable/helpers/make-table-scrollable-loading-columns";
import { MuiTableScrollable } from "src/components-mui/base/table-scrollable/table-scrollable";
import { TableFirstColumnWidth } from "src/components-mui/base/table-scrollable/table-scrollable.types";
import { getTableDataForLoading } from "src/components-mui/base/table-scrollable/utils";
import { Typography } from "src/components-mui/base/typography/typography";

interface ITableEmptyStateProps<T extends object> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    columns: ColumnDef<T, any>[] | (() => ColumnDef<T, any>[]);
    emptyStateText: string;
    isFirstCellAvatar?: boolean;
    firstColumnWidth?: TableFirstColumnWidth;
}

export const TableEmptyState = <T extends object>({
    columns,
    emptyStateText,
    firstColumnWidth,
    isFirstCellAvatar = true,
}: ITableEmptyStateProps<T>) => {
    const emptyColumns = useMemo(
        () =>
            typeof columns === "function"
                ? columns()
                : makeTableScrollableLoadingColumns({
                      columns,
                      isFirstCellAvatar,
                      disableAnimation: true,
                      skeletonBackgroundColor: "gray.50",
                      firstColumnWidth,
                  }),
        [columns, isFirstCellAvatar, firstColumnWidth]
    );

    const emptyData = useMemo(() => getTableDataForLoading(6, 0, 6), []);

    return (
        <Box
            sx={{
                position: "relative",
                width: "100%",
                height: "100%",
            }}
        >
            <MuiTableScrollable data={emptyData as T[]} columns={emptyColumns} />
            <Box
                sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 1,
                    pointerEvents: "auto",
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 3,
                        height: "100%",
                    }}
                >
                    <IconNoData sx={{ width: "141px", height: "116px" }} />
                    <Typography
                        sx={{
                            fontWeight: "bold",
                        }}
                    >
                        {emptyStateText}
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
};
